import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, notification } from 'antd';
import { FC, useEffect, useState } from 'react';

import { errorObjectToString, formatNumber } from 'src/utils/common';
import { formatDate, getServerTime } from 'src/utils/date-util';

import useHyosungCMSMembers from 'src/hooks/useHyosungCMSMembers';
import useStores from 'src/hooks/useStores';

import Loading from 'src/components/Loading/Loading';
import InvoiceCreationTable, { InvoiceCreationItem } from 'src/components/NewSettlement/InvoiceTable/InvoiceCreationTable';

import InProgressTasksModal, { InProgressTaskItem } from './components/InProgressTasksModal';
import UnpaidStoresModal from './components/UnpaidStoresModal';
import useInvoiceCreationData from './hooks/useInvoiceCreationData';
import {
  convertToInProgressTaskItems,
  createInvoicesWithUpdateChildren,
  filterConflictingTasks,
} from './utils/invoice-creation-utils';

const InvoiceCreationPage: FC = () => {
  const stores = useStores();
  const hyosungCMSMembers = useHyosungCMSMembers();

  const { invoiceCreationItems, inProgressTasks, unpaidStoreItems, overpaidStoreItems, totalUnpaidInfo } =
    useInvoiceCreationData({
      stores,
      hyosungCMSMembers,
    });

  const [selectedRows, setSelectedRows] = useState<InvoiceCreationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [unpaidModalOpen, setUnpaidModalOpen] = useState(false);
  const [overpaidModalOpen, setOverpaidModalOpen] = useState(false);
  const [inProgressModalOpen, setInProgressModalOpen] = useState(false);
  const [conflictingTasks, setConflictingTasks] = useState<InProgressTaskItem[]>([]);
  const [serverDate, setServerDate] = useState<string>('');

  useEffect(() => {
    getServerTime().then((time) => {
      setServerDate(formatDate(time, 'yyyy-MM-dd'));
    });
  }, []);

  const handleShowInProgressTasks = () => {
    setConflictingTasks(convertToInProgressTaskItems(inProgressTasks));
    setInProgressModalOpen(true);
  };

  const handleCreateInvoices = async () => {
    try {
      if (selectedRows.length <= 0) {
        notification.error({ message: '선택된 매장이 없습니다.' });
        return;
      }

      // IN_PROGRESS 상태이면서 targetDate가 오늘인 태스크 확인
      const selectedStoreIds = new Set(selectedRows.map((row) => row.storeId));
      const conflicting = filterConflictingTasks(inProgressTasks, selectedStoreIds, serverDate);

      if (conflicting.length > 0) {
        setConflictingTasks(convertToInProgressTaskItems(conflicting));
        setInProgressModalOpen(true);
        return;
      }

      setLoading(true);
      await createInvoicesWithUpdateChildren(selectedRows.map((item) => item.invoice));
      notification.success({ message: `${selectedRows.length}개의 청구서를 생성했습니다.` });
      setSelectedRows([]);
    } catch (error) {
      console.error(error);
      const description = errorObjectToString(error);
      notification.error({ message: '청구서 생성 실패', description });
    }
    setLoading(false);
  };

  return (
    <>
      {loading && <Loading title='잠시만 기다려주세요.' />}
      <div className='tabBody flexColumn height100'>
        <section className='flexRow flexSpaceBetween bgWhite'>
          <div className='flexRow flexAlignCenter' style={{ gap: 8 }}>
            <Button
              type='primary'
              loading={loading}
              disabled={selectedRows.length <= 0 || !serverDate}
              onClick={handleCreateInvoices}>
              {selectedRows.length}개 청구서 생성
            </Button>
            <span style={{ color: '#666' }}>기준일: {serverDate || '-'}</span>
            {inProgressTasks.length > 0 && (
              <Button type='link' danger icon={<InfoCircleOutlined />} onClick={handleShowInProgressTasks}>
                진행중 태스크: {inProgressTasks.length}건
              </Button>
            )}
          </div>
          <div className='flexRow flexAlignCenter' style={{ gap: 16 }}>
            <span>
              미수금: <b style={{ color: 'red' }}>{formatNumber(totalUnpaidInfo.totalUnpaidAmount)}</b>원
              {' / '}
              <Button type='link' style={{ padding: 0 }} onClick={() => setUnpaidModalOpen(true)}>
                {totalUnpaidInfo.unpaidStoreCount}건
              </Button>
            </span>
            <span>
              초과납부: <b style={{ color: 'blue' }}>{formatNumber(totalUnpaidInfo.totalOverpaidAmount)}</b>원
              {' / '}
              <Button type='link' style={{ padding: 0 }} onClick={() => setOverpaidModalOpen(true)}>
                {totalUnpaidInfo.overpaidStoreCount}건
              </Button>
            </span>
          </div>
        </section>
        <section className='flexRow height100'>
          <InvoiceCreationTable invoiceCreationItems={invoiceCreationItems} setSelectedRows={setSelectedRows} />
        </section>
      </div>

      <UnpaidStoresModal
        open={unpaidModalOpen}
        onClose={() => setUnpaidModalOpen(false)}
        items={unpaidStoreItems}
        title='미수 매장 목록'
        type='unpaid'
      />

      <UnpaidStoresModal
        open={overpaidModalOpen}
        onClose={() => setOverpaidModalOpen(false)}
        items={overpaidStoreItems}
        title='초과납부 매장 목록'
        type='overpaid'
      />

      <InProgressTasksModal
        open={inProgressModalOpen}
        onClose={() => setInProgressModalOpen(false)}
        items={conflictingTasks}
      />
    </>
  );
};

export default InvoiceCreationPage;
